import {
  TPageableDataWithContentDB,
  TPageableDataWithContentUI,
} from 'data/types/generic.types';

export enum EWorkshopTaskStatus {
  TO_DO = 'TO_DO',
  IN_PROGRESS = 'IN_PROGRESS',
  STALLED = 'STALLED',
  COMPLETED = 'COMPLETED',
}

export enum EWorkshopPartType {
  PREP = 'PREP',
  STANDALONE = 'STANDALONE',
  PART_ONE = 'PART_ONE',
  PART_TWO = 'PART_TWO',
  PART_THREE = 'PART_THREE',
  POST = 'POST',
}

export type TWorkshopTaskDB = {
  resourceId: string;
  name: string;
  order: number;
  status: EWorkshopTaskStatus;
  partType: EWorkshopPartType;
  dueDate: string;
  assignedTo?: string | null;
  /** GROW-4249: true for tasks added by an admin, false for seeded template tasks */
  custom?: boolean;
};

export type TWorkshopTaskUI = {
  id: string;
  taskName: string;
  order: number;
  status: EWorkshopTaskStatus;
  part: EWorkshopPartType;
  dueDate: string;
  assignee: string;
  custom: boolean;
};

export type TGetWorkshopTasksRequest = {
  workshopId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
  partType?: EWorkshopPartType[];
  status?: EWorkshopTaskStatus[];
  assignee?: string;
};

export enum EWorkshopTasksFilterColumn {
  PART = 'part',
  STATUS = 'status',
  ASSIGNEE = 'assignee',
}

// Must match the BE WorkshopTaskSortField enum value (case-insensitive):
// dueDate / order / name / partType. Sent verbatim as the sortField param.
export const WORKSHOP_TASKS_DUE_DATE_SORT_FIELD = 'dueDate';

// Body of POST /workshops/tasks/export (BE ExportWorkshopTasksRequest,
// GROW-4164). resourceIds switches Export All → Export Selected; the BE
// intersects them with the other filters. Omitted sort falls back to the
// BE default (order ASC), which matches the part-ordered default view.
export type TExportWorkshopTasksFilters = {
  workshopId: string;
  partType?: EWorkshopPartType[];
  status?: EWorkshopTaskStatus[];
  assignee?: string;
  taskName?: string;
  resourceIds?: string[];
};

export type TExportWorkshopTasksRequest = {
  columns: string[];
  filters: TExportWorkshopTasksFilters;
  sort?: { field: string; direction: 'ASC' | 'DESC' };
};

export type TWorkshopTasksPageDB = TPageableDataWithContentDB<
  TWorkshopTaskDB[]
>;
export type TWorkshopTasksPageUI = TPageableDataWithContentUI<
  TWorkshopTaskUI[]
>;

export const WORKSHOP_TASKS_DEFAULT_PAGE_SIZE = 50;

// dayjs format used for displaying + PATCHing task due dates.
// Must stay MM/DD/YYYY: the BE PATCH expects MM/dd/yyyy (GROW-3789) and the
// due-date cell parses incoming values with this same format.
export const WORKSHOP_TASK_DATE_FORMAT = 'MM/DD/YYYY';

export type TUpdateWorkshopTaskStatusRequest = {
  workshopId: string;
  taskId: string;
  status: EWorkshopTaskStatus;
};

// Body of PATCH /workshops/tasks/name (BE UpdateTaskNameCommand). The BE
// trims the name and rejects blank values. The FE only offers this on custom
// tasks (GROW-4278); seeded template task names stay fixed.
export type TUpdateWorkshopTaskNameRequest = {
  workshopId: string;
  taskId: string;
  name: string;
};

export type TUpdateWorkshopTaskDueDateRequest = {
  workshopId: string;
  taskId: string;
  dueDate: string | null;
};

export type TUpdateWorkshopTaskAssigneeRequest = {
  workshopId: string;
  taskId: string;
  userId: string;
  userName: string;
};

// BE returns UserIdentityDto: { id, fullName, email } (GROW-2552).
export type TWorkshopTaskAssigneeDB = {
  id: string;
  fullName: string;
  email?: string;
};

export type TWorkshopTaskAssigneeUI = {
  userId: string;
  name: string;
};

export type TGetWorkshopTaskAssigneesRequest = {
  workshopId: string;
  assignee: string;
};

// Body of POST /workshops/tasks (BE AddCustomTaskCommand, GROW-4249). All four
// fields are required server-side; dueDate is sent in WORKSHOP_TASK_DATE_FORMAT
// (the BE example is "06/15/2025"). Custom tasks belong to one workshop only.
export type TAddCustomWorkshopTaskRequest = {
  workshopId: string;
  name: string;
  partType: EWorkshopPartType;
  dueDate: string;
};

export type TDeleteCustomWorkshopTaskRequest = {
  workshopId: string;
  taskId: string;
};
